import React, { useEffect } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import DetailsPageTitle from "components/common/DetailsPageTitle";
import NoDataFound from "components/common/NoDataFound";
import { DollarSign, MapPin, User, UserRound, Map } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getWorkspaceDetails } from "store/actions/workspaceSlice";
import Spinners from "components/common/Spinners";
import Riyal from "assets/images/icons/riyal.svg";
import riyalGray2 from "assets/images/icons/riyalGray2.svg";

const HotDeskDetails = () => {
  const { t } = useTranslation();
  document.title = t("Hot Desk Details | Work Space");
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { loading, workspaceDetails } = useSelector((state) => state.Workspace);

  useEffect(() => {
    const getDetails = () => {
      const payload = {
        id,
      };
      dispatch(getWorkspaceDetails(payload));
    };
    getDetails();
  }, []);

  // Extract lowest and highest seat and price
  const timePeriods = workspaceDetails?.timePeriods || [];
  const lowestSeats = Math.min(
    ...timePeriods.map((item) => item.numberOfSeats)
  );
  const highestSeats = Math.max(
    ...timePeriods.map((item) => item.numberOfSeats)
  );

  const lowestPrice = Math.min(...timePeriods.map((item) => item.price));
  const highestPrice = Math.max(...timePeriods.map((item) => item.price));

  return (
    <React.Fragment>
      <div className="page-content common_details_page workspace_details_page">
        {loading ? (
          <Spinners />
        ) : (
          <Container fluid>
            <Row>
              <DetailsPageTitle title={t("Hot Desk Details")} />
            </Row>
            <div className="common_wrapper">
              <Row>
                <Col lg={7} className="images_section">
                  <div className="d-flex gap-3">
                    <div className="left_section">
                      {workspaceDetails?.imageUrls?.[0] && (
                        <img
                          src={workspaceDetails.imageUrls[0]}
                          alt="Main Image"
                        />
                      )}
                    </div>
                    <div
                      className="right_section"
                      style={{
                        gridTemplateColumns:
                          workspaceDetails?.imageUrls?.length <= 3
                            ? "1fr"
                            : "repeat(2, 1fr)",
                      }}
                    >
                      {workspaceDetails?.imageUrls
                        ?.slice(1)
                        .map((url, index) => (
                          <img
                            key={index}
                            src={url}
                            alt={`Office Image ${index + 2}`}
                          />
                        ))}
                    </div>
                  </div>
                </Col>
                <Col lg={5}>
                  <div className="time_periods"></div>
                </Col>

                <Col lg={12} className="mt-3">
                  <div className="title_section">
                    <h2 className="fs_24">
                      {workspaceDetails?.name},{" "}
                      {workspaceDetails?.nameAr || "-"}
                    </h2>
                    <p className="mt-3 fs_14 text_gray d-flex align-items-center gap-4">
                      <span>
                        <MapPin
                          className="text_gray me-1"
                          width={14}
                          height={14}
                        />
                        {workspaceDetails?.cityId?.name || "-"}
                      </span>
                      <span>
                        <Map
                          className="text_gray me-1"
                          width={14}
                          height={14}
                        />
                        {workspaceDetails?.branchId?.name || "-"}
                      </span>
                    </p>
                  </div>
                </Col>
                <Col lg={12}>
                  <h3 className="fs_20 mt-3">
                    {t("Remaining Seats")}: {workspaceDetails?.seatsRemaining}
                  </h3>
                  <h3 className="fs_20 mt-3">
                    {t("Total Seats")}: {workspaceDetails?.totalSeats}
                  </h3>
                </Col>
                <Col lg={12} className="mt-3">
                  <div className="description_row">
                    <h2 className="fs_20">{t("Description")}</h2>
                    <div className="description_inner">
                      <div className="w-50 me-1">
                        <p>{workspaceDetails?.description || "-"}</p>
                      </div>
                      <div className="divider"></div>
                      <div className="w-50 rtl ms-1">
                        <p>{workspaceDetails?.descriptionAr || "-"}</p>
                      </div>
                    </div>
                  </div>
                </Col>
                <Col lg={12} className="mt-3">
                  <div className="amenities_row">
                    <h2 className="fs_20">{t("Amenities")}</h2>
                    <div className="amenities_inner">
                      {workspaceDetails?.amenities?.length === 0 ? (
                        <div className="b_r_20 bg-white w-100 pb-3">
                          <NoDataFound className="h_fit_content" />
                        </div>
                      ) : (
                        workspaceDetails?.amenities?.map((amenity) => (
                          <div className="single_amenity" key={amenity?._id}>
                            <img
                              src={amenity?.imageUrl}
                              alt={amenity?.name}
                              width={24}
                              height={24}
                            />
                            <p className="fs_14">{amenity.name || "-"}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className="mt-4">
                {/* One Day Pass as separate column */}
                {workspaceDetails?.oneDayPass && (
                  <Col lg={4}>
                    <div className="time_periods m-0">
                      <h2 className="fs_20">{t("One Day Plan")}</h2>
                      <div className="time_period_inner">
                        <div className="single_time_period mb-3 p-3 border rounded">
                          <h2 className="fs_16 text_dark_gray mb-2">
                            {workspaceDetails.oneDayPass.name}
                          </h2>
                          <h2 className="fs_14 mb-1 d-flex align-items-center gap-1">
                            <img
                              src={Riyal}
                              width={12}
                              height={12}
                              alt="riyal"
                              className="me-1"
                            />
                            {workspaceDetails.oneDayPass.price}
                          </h2>
                        </div>
                      </div>
                    </div>
                  </Col>
                )}

                {["monthlyPlans", "yearlyPlans"]
                  .filter((key) => workspaceDetails?.[key]?.length > 0)
                  .map((planTypeKey, idx, filtered) => {
                    const plans = workspaceDetails[planTypeKey];

                    return (
                      <Col lg={4} key={planTypeKey}>
                        <div className="time_periods m-0">
                          <h2 className="fs_20">
                            {planTypeKey === "monthlyPlans" &&
                              t("Monthly Plans")}
                            {planTypeKey === "yearlyPlans" && t("Yearly Plans")}
                          </h2>
                          <div className="time_period_inner">
                            {plans.length === 0 ? (
                              <NoDataFound />
                            ) : (
                              plans.map((plan) => (
                                <div
                                  className="single_time_period mb-3 p-3 border rounded"
                                  key={plan?._id}
                                >
                                  {/* Plan Title */}
                                  <h2 className="fs_16 text_dark_gray mb-2">
                                    {plan?.name}
                                  </h2>

                                  {/* Duration */}
                                  {plan?.duration && (
                                    <h2 className="fs_14 mb-1">
                                      {t("Duration")}: {plan?.duration}{" "}
                                      {planTypeKey === "timePeriods"
                                        ? t("Day(s)")
                                        : t("Month(s)")}
                                    </h2>
                                  )}

                                  {/* Price & Discount */}
                                  <h2 className="fs_14 mb-1 d-flex align-items-center gap-1">
                                    <img
                                      src={Riyal}
                                      width={12}
                                      height={12}
                                      alt="riyal"
                                      className="me-1"
                                    />
                                    {plan?.price}
                                    {plan?.discountPrice && (
                                      <span className="text-success">
                                        ({t("Discount")}: {plan?.discountPrice})
                                      </span>
                                    )}
                                  </h2>

                                  {/* Features */}
                                  {plan?.features?.length > 0 && (
                                    <ul className="fs_13 mt-2 ms-3 text_gray">
                                      {plan.features.map((f, idx) => (
                                        <li key={idx}>{f?.title}</li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </Col>
                    );
                  })}
              </Row>
            </div>
            <Row>
              <div className="g_end mt_32 mb-2">
                <button
                  className="main_btn"
                  onClick={() => {
                    navigate(
                      `/bookings/hot-desks/edit-hot-desk?id=${workspaceDetails?._id}`
                    );
                  }}
                >
                  {t("Edit")}
                </button>
              </div>
            </Row>
          </Container>
        )}
      </div>
    </React.Fragment>
  );
};

export default HotDeskDetails;
